import React, { useState, useEffect } from 'react';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import {
  Box,
  Button,
  ButtonGroup,
  Paper,
  Tab,
  Tabs
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& > *': {
      margin: theme.spacing(1)
    },
  },
}));

const SalesTable = (props) => {
  
  const classes = useStyles();
  
  const [currentTable, setCurrentTable] = useState([]);
  const [month, setMonth] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  const columns = [
    { label: "Day", name: "day" },
    { label: "Month", name: "month" },
    { label: "Year", name: "year" },
    { label: "Total Sales", name: "sales" },
    { label: "Purchase Orders", name: "purchaseOrders" },
    { label: "Expenses", name: "expenses" },
    {
      label: "Profit",
      name: "profit",
      options: {
        filter: true,
        sort: true,
        viewColumns: false,
        customBodyRender: (value, tableMeta) => (
          <font color={ value < 0 ? "red" : null }>{value}</font>
        )
      }
    }
  ];

  //Initially, The table will be set with the monthly data
  useEffect(() => {
    const result = [];
    let startDate = moment(props.dates.startDate);

    //The additional 'OR' condition is important to make sure the loop runs even if the months are the same.
    while (startDate.isBefore(props.dates.endDate) || startDate.isSame(props.dates.endDate, 'month')) {

      let totalMonthSales = props.salesOrders.reduce((prevSale, currentSale) => {
        if(startDate.isSame(currentSale.createdAt, 'month')) return prevSale + currentSale.total;
        else return prevSale;
      }, 0);

      let totalMonthPO = 0;
      for ( let i = 0; i < props.purchaseOrders.length; i++ ) {
        let purchaseOrderTotal = 0;
        for ( let j = 0; j < props.purchaseOrders[i].payments.length; j++ ) {
          const currentPayment = props.purchaseOrders[i].payments[j];
          if(startDate.isSame(currentPayment.createdAt, 'month')) {
            purchaseOrderTotal += currentPayment.amount;
          }
        }
        totalMonthPO += purchaseOrderTotal;
      }

      let totalMonthRO = props.returnOrders.reduce((prevRO, currentRO) => {
        if(startDate.isSame(currentRO.createdAt, 'month')) return prevRO + currentRO.total;
        else return prevRO;
      }, 0);

      let totalMonthExpenses = props.expenses.reduce((prevExpense, currentExpense) => {
        if(startDate.isSame(currentExpense.createdAt, 'month')) return prevExpense + currentExpense.total;
        else return prevExpense;
      }, 0);

      result.push({
        month: startDate.format("MMMM"),
        year: startDate.format("YYYY"),
        sales: totalMonthSales,
        purchaseOrders: totalMonthPO,
        expenses: totalMonthExpenses,
        profit: ( totalMonthSales + totalMonthRO ) - ( totalMonthExpenses + totalMonthPO )
      });
      startDate.add(1, 'month');
    }

    setCurrentTable(result.reverse());

  },[props.salesOrders, props.expenses, props.purchaseOrders, props.returnOrders, props.dates])

  const options = {
    selectableRows: 'none',
    viewColumns: false
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  //Function to group Sales by Day
  const groupByDay = () => {
    const result = [];
    let startDate = moment(props.dates.startDate);

    while (startDate.isBefore(props.dates.endDate) || startDate.isSame(props.dates.endDate, 'day')) {

      let totalDaySales = props.salesOrders.reduce((prevSale, currentSale) => {
        if(startDate.isSame(currentSale.createdAt, 'day')) return prevSale + currentSale.total;
        else return prevSale;
      }, 0);

      let totalDayPO = 0;
      for ( let i = 0; i < props.purchaseOrders.length; i++ ) {
        let purchaseOrderTotal = 0;
        for ( let j = 0; j < props.purchaseOrders[i].payments.length; j++ ) {
          const currentPayment = props.purchaseOrders[i].payments[j];
          if(startDate.isSame(currentPayment.createdAt, 'day')) {
            purchaseOrderTotal += currentPayment.amount;
          }
        }
        totalDayPO += purchaseOrderTotal;
      }

      let totalDayRO = props.returnOrders.reduce((prevRO, currentRO) => {
        if(startDate.isSame(currentRO.createdAt, 'day')) return prevRO + currentRO.total;
        else return prevRO;
      }, 0);

      let totalDayExpenses = props.expenses.reduce((prevExpense, currentExpense) => {
        if(startDate.isSame(currentExpense.createdAt, 'day')) return prevExpense + currentExpense.total;
        else return prevExpense;
      }, 0);

      result.push({
        day: `${startDate.format("dddd")}, ${startDate.format("DD/MM")}`,
        year: startDate.format("YYYY"),
        sales: totalDaySales,
        purchaseOrders: totalDayPO,
        expenses: totalDayExpenses,
        profit: ( totalDaySales + totalDayRO ) - ( totalDayExpenses + totalDayPO )
      });
      startDate.add(1, 'day');
    }

    setCurrentTable(result.reverse());
    setMonth(false);
  };

  //Function to group Sales by Month
  const groupByMonth = () => {
    const result = [];
    let startDate = moment(props.dates.startDate);

    //The additional 'OR' condition is important to make sure the loop runs even if the months are the same.
    while (startDate.isBefore(props.dates.endDate) || startDate.isSame(props.dates.endDate, 'month')) {
      
      let totalMonthSales = props.salesOrders.reduce((prevSale, currentSale) => {
        if(startDate.isSame(currentSale.createdAt, 'month')) return prevSale + currentSale.total;
        else return prevSale;
      }, 0);

      let totalMonthPO = 0;
      for ( let i = 0; i < props.purchaseOrders.length; i++ ) {
        let purchaseOrderTotal = 0;
        for ( let j = 0; j < props.purchaseOrders[i].payments.length; j++ ) {
          const currentPayment = props.purchaseOrders[i].payments[j];
          if(startDate.isSame(currentPayment.createdAt, 'month')) {
            purchaseOrderTotal += currentPayment.amount;
          }
        }
        totalMonthPO += purchaseOrderTotal;
      }

      let totalMonthRO = props.returnOrders.reduce((prevRO, currentRO) => {
        if(startDate.isSame(currentRO.createdAt, 'month')) return prevRO + currentRO.total;
        else return prevRO;
      }, 0);

      let totalMonthExpenses = props.expenses.reduce((prevExpense, currentExpense) => {
        if(startDate.isSame(currentExpense.createdAt, 'month')) return prevExpense + currentExpense.total;
        else return prevExpense;
      }, 0);

      result.push({
        month: startDate.format("MMMM"),
        year: startDate.format("YYYY"),
        sales: totalMonthSales,
        purchaseOrders: totalMonthPO,
        expenses: totalMonthExpenses,
        profit: ( totalMonthSales + totalMonthRO ) - ( totalMonthExpenses + totalMonthPO )
      });
      startDate.add(1, 'month');
    }

    setCurrentTable(result.reverse());
    setMonth(true);
  };

  return (
    <React.Fragment>
      <div className={classes.root}>
        <Button
          color="primary"
          variant="contained"
          onClick={props.hideSalesTable}
        >
          Return
        </Button>
        <ButtonGroup variant="text" color="primary" aria-label="text primary button group">
          <Button
            onClick={groupByDay}
          >
            Daily
          </Button>
          <Button
            onClick={groupByMonth}
          >
            Monthly
          </Button>
        </ButtonGroup>
      </div>
      <Box sx={{display: 'flex'}}>
        <Paper>
          <Tabs
            value={tabValue}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleTabChange}
            centered
          >
            <Tab label="Sales"/>
            <Tab label="Expenses"/>
            <Tab label="Profits"/>
          </Tabs>
        </Paper>
      </Box>
      <Box>
        { tabValue === 0 ?
        <MUIDataTable
          title={"Sales"}
          data={currentTable}
          columns={ month ?
            columns.filter(column => ["month", "year", "sales"].includes(column.name))
            : columns.filter(column => ["day", "year", "sales"].includes(column.name))
          }
          options={options}
        />
        : null }
        { tabValue === 1 ?
        <MUIDataTable
          title={"Expenses"}
          data={currentTable}
          columns={ month ?
            columns.filter(column => !["day", "sales", "profit"].includes(column.name))
            : columns.filter(column => !["month", "sales", "profit"].includes(column.name))
          }
          options={options}
        />
        : null }
        { tabValue === 2 ?
        <MUIDataTable
          title={"Profits"}
          data={currentTable}
          columns={ month ? columns.filter(column => column.name !== "day") : columns.filter(column => column.name !== "month")}
          options={options}
        />
        : null }
      </Box>
    </React.Fragment>
  )
};

export default SalesTable;